import mapValues from 'lodash-es/mapValues'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Dashboard } from '@components/common/Dashboard/Dashboard'
import { PageLayout } from '@components/common/PageLayout'
import { BackButton, Banner, Loader, Toasts } from '@fourthwall/components'
import { OffersApiClient } from '@fourthwall/services/dist/OffersApiClient'
import { reverse, routing } from '@utils/routing'

import { useOfferQuery } from '../hooks/useOfferQuery'
import type { FormValues } from './CostsForm'
import { CostsForm } from './CostsForm'
import { hasAnyVariantZeroCost } from './utils'

export const Costs = () => {
  const { productId = '' } = useParams<'productId'>()
  const { offerQuery, invalidateOfferQuery } = useOfferQuery([productId])
  const data = offerQuery.data
  const name = data?.name || ''
  const variants = data?.variants || []
  const unavailableVariants = variants.filter((variant) => variant.status === 'UNAVAILABLE')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isFetching = offerQuery.isLoading
  const displayZeroCostWarning = hasAnyVariantZeroCost(variants)

  const initialValues = variants.reduce(
    (accumulator, currentValue) => ({
      ...accumulator,
      [currentValue.id]: currentValue.cost?.value,
    }),
    {},
  )

  const handleSubmit = async (values: FormValues) => {
    setIsSubmitting(true)

    try {
      const payload = {
        costs: mapValues(values, (value) => ({ value: parseFloat(value), currency: 'USD' })),
      }
      await OffersApiClient.replaceVariantsCosts(payload)

      Toasts.notify('success', { type: 'info' })
      setIsSubmitting(false)

      invalidateOfferQuery()
    } catch {
      Toasts.notify('error', { type: 'error' })
      setIsSubmitting(false)
    }
  }

  const renderContent = () => {
    if (!data || isFetching) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Loader size="large" />
        </div>
      )
    }

    return (
      <>
        {data.isSettingCostsRequired && (
          <div style={{ marginBottom: 16 }}>
            <Banner appearance="warning">There are variants with missing cost value.</Banner>
          </div>
        )}
        {unavailableVariants.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Banner appearance="warning">
              There are {unavailableVariants.length} variants unavailable to buy at the moment.
            </Banner>
          </div>
        )}
        {displayZeroCostWarning && (
          <div style={{ marginBottom: 16 }}>
            <Banner appearance="warning">Some of the variants have costs set to 0.</Banner>
          </div>
        )}
        <CostsForm
          variants={variants}
          initialValues={initialValues}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
        />
      </>
    )
  }

  return (
    <PageLayout size="small">
      <Dashboard.Section
        name="Variants Costs"
        topContent={
          name && (
            <BackButton
              label={name}
              to={reverse(routing.products.all.product.self, { productId })}
            />
          )
        }
      >
        {renderContent()}
      </Dashboard.Section>
    </PageLayout>
  )
}
